// src/common/file-catalog.js
import { getCommonBasePath, getRelativePath } from './utils/path-utils.js';

/**
 * Manages the file catalog for displaying file information and resolving source URLs.
 */
export class FileCatalogManager {
  constructor() {
    this.catalog = new Map();
    this.sourceBasePath = '';
    this.apiBase = null;
    this.currentDocumentId = null;
    this.platform = null;
    this.clientId = null;
    this.getAuthHeadersFn = null;
  }

  setApiBase(apiBase) {
    this.apiBase = apiBase;
  }

  setCurrentDocumentId(documentId) {
    this.currentDocumentId = documentId;
  }

  setPlatform(platform) {
    this.platform = platform;
  }

  setClientId(clientId) {
    this.clientId = clientId;
  }

  setGetAuthHeadersFn(fn) {
    this.getAuthHeadersFn = fn;
  }

  async getAuthHeaders() {
    if (!this.getAuthHeadersFn) {
      return {};
    }
    try {
      const token = await this.getAuthHeadersFn();
      return { 'Authorization': `Bearer ${token}` };
    } catch (error) {
      console.warn('[FileCatalogManager] Failed to get access token:', error);
      return {};
    }
  }

  setFileCatalog(files = [], sourceBasePath = '') {
    this.catalog.clear();
    this.sourceBasePath = typeof sourceBasePath === 'string' ? sourceBasePath : '';
    if (!this.sourceBasePath) {
      this.sourceBasePath = getCommonBasePath(files);
    }
    files.forEach(file => {
      if (!file || file.file_id === undefined || file.file_id === null) return;
      const id = String(file.file_id);
      this.catalog.set(id, {
        ...file,
        _basePath: this.sourceBasePath,
        displayPath: file.path || '',
      });
    });
  }

  async ensureFileCatalog() {
    if (this.catalog.size > 0) return Array.from(this.catalog.values());
    if (!this.apiBase || !this.currentDocumentId) return [];

    try {
      const documentPath = this.platform?.getDocumentPath ? await this.platform.getDocumentPath() : null;
      const params = new URLSearchParams({ document_id: this.currentDocumentId });
      if (documentPath) params.append('document_path', documentPath);
      if (this.clientId) params.append('client_id', this.clientId);

      const authHeaders = await this.getAuthHeaders();
      const res = await fetch(`${this.apiBase}/db/files?${params}`, {
        headers: authHeaders
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const files = Array.isArray(data?.files) ? data.files : [];
      const sourceBasePath = typeof data?.source_folder === 'string' ? data.source_folder : '';
      this.setFileCatalog(files, sourceBasePath);
      return Array.from(this.catalog.values());
    } catch (err) {
      console.error('Failed to load file catalog:', err);
      return [];
    }
  }

  getFileDisplay(entry) {
    const fileId = entry?.file ?? entry?.file_id ?? entry?.resolve_result?.file_id;
    const idStr = fileId === undefined || fileId === null ? null : String(fileId);
    const sourceDb = entry?.source_db || entry?.resolve_result?.source_db || 'doc';
    const catalogInfo = idStr && sourceDb === 'doc' ? this.catalog.get(idStr) : null;
    
    const path = entry?.file_path || entry?.resolve_result?.file_path || catalogInfo?.path || catalogInfo?.displayPath;
    const title = entry?.file_title || entry?.resolve_result?.file_title || catalogInfo?.title;
    const downloaded = entry?.source_url || entry?.resolve_result?.url || catalogInfo?.source_url;

    let label = '—';
    if (title && (downloaded || sourceDb !== 'doc' || /(?:tmp|temp)[a-z0-9_-]{6,}|resolved_sources/i.test(path || ''))) {
      label = title;
    } else if (path) {
      // Show relative path from common base to display folder hierarchy
      const allFiles = Array.from(this.catalog.values());
      const basePath = this.sourceBasePath || getCommonBasePath(allFiles);
      label = getRelativePath(path, basePath);
    } else if (title) {
      // Fall back to title if no path available
      label = title;
    } else if (idStr) {
      label = `File ${idStr}`;
    }

    return { fileId: idStr, label, info: catalogInfo };
  }

  // file_id is per-database: only the document's own catalog is loaded here, so
  // a library/peer-owned id ('library:…', 'document:…') must not be looked up in it.
  getFileSourceUrl(fileId, sourceDb = 'doc') {
    if (!fileId) return null;
    if (sourceDb && sourceDb !== 'doc') return null;
    const idStr = String(fileId);
    const catalogInfo = this.catalog.get(idStr);
    return catalogInfo?.source_url || null;
  }

  clear() {
    this.catalog.clear();
    this.sourceBasePath = '';
  }

  get size() {
    return this.catalog.size;
  }
}

